/**
 * Moves an element to a new position with a specified speed and returns a Promise that resolves when the animation is complete.
 * @param {HTMLElement} element - The element to move.
 * @param {number} newX - The new X-coordinate of the element.
 * @param {number} newY - The new Y-coordinate of the element.
 * @param {number} speed - The speed in pixels per second.
 * @returns {Promise} A Promise that resolves when the animation is complete.
 */
import {distanceToInches, parseImperialFractionToDecimal} from "./sqftHelper";
import EventManager from "../components/canvas/store/sqftEventManager";
import sqftConfig from "../components/canvas/store/sqftConfig";
import {MEASURE_UNITS} from "../constants";

export const moveElement = (element, newX, newY, speed) => {
  return new Promise(resolve => {
    // Get the current coordinates of the element
    const oldX = element.offsetLeft;
    const oldY = element.offsetTop;

    // Calculate the distance between the old and new coordinates
    const distanceX = newX - oldX;
    const distanceY = newY - oldY;

    // Calculate the duration of the animation based on the speed
    const duration = Math.sqrt(distanceX ** 2 + distanceY ** 2) / speed;

    // Set the element to the initial position
    element.style.position = "absolute";
    element.style.left = oldX + "px";
    element.style.top = oldY + "px";

    // Animate the element to the new position
    const startTime = performance.now();
    function animate(currentTime) {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const x = oldX + distanceX * progress;
      const y = oldY + distanceY * progress;
      element.style.left = x + "px";
      element.style.top = y + "px";
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve(); // Animation complete, resolve the Promise
      }
    }
    requestAnimationFrame(animate);
  });
}

export const delay = (time) => {
  return new Promise(resolve => setTimeout(resolve, time));
}

export function createAdjustableInput({
                                        position = { x: 0, y: 0 },
                                        initialValue = "",
                                        min = 1,
                                        max = 1000,
                                        orientation = "horizontal",
                                        onChange = () => {},
                                        onClose = () => {},
                                        step = 1,
                                        longPressInterval = 50,
                                        longPressDelay = 200,
                                      }) {
  const objectName = { name: 'lineInput' };
  let isClosed = false;

  const inputWrapper = document.createElement("div");
  inputWrapper.className = "line-input";
  inputWrapper.style.position = "absolute";
  inputWrapper.style.left = `${position.x}px`;
  inputWrapper.style.top = `${position.y}px`;

  if (orientation === "horizontal") {
    inputWrapper.classList.add("line-input-horizontal");
  } else if (orientation === "vertical") {
    inputWrapper.classList.add("line-input-vertical");
  }

  const input = document.createElement("input");
  input.type = "text";
  input.id = "line-input-field";
  input.className = "line-input-field";
  input.setAttribute("min", min.toString());
  input.setAttribute("max", max.toString());
  input.setAttribute("autocomplete", "off");
  input.setAttribute("autocapitalize", "off");
  input.setAttribute("autocorrect", "off");
  input.spellcheck = false;
  input.value = initialValue;

  const adjustInputValue = (delta) => {
    let currentValue = parseImperialFractionToDecimal(input.value);
    currentValue += delta;
    currentValue = Math.max(min, Math.min(currentValue, max));
    onChange(currentValue);

    input.value = sqftConfig.measureUnit === MEASURE_UNITS.Imperial
      ? distanceToInches(currentValue).replace("\"", "")
      : currentValue;
    input.dispatchEvent(new Event("change"));
  };

  let longPressTimeout;
  let delayTimeout;
  const startLongPress = (delta) => {
    adjustInputValue(delta);
    delayTimeout = setTimeout(() => {
      longPressTimeout = setInterval(() => adjustInputValue(delta), longPressInterval);
    }, longPressDelay);
  };
  const stopLongPress = () => {
    clearInterval(longPressTimeout);
    clearTimeout(delayTimeout);
    input.focus();
  };

  const incrementButton = document.createElement("button");
  incrementButton.className = "increment-button";
  incrementButton.type = "button";
  incrementButton.tabIndex = -1;
  incrementButton.textContent = "▲";
  incrementButton.onmousedown = (e) => {
    e.preventDefault();
    startLongPress(step);
  };
  incrementButton.onmouseup = stopLongPress;
  incrementButton.onmouseleave = stopLongPress;

  const decrementButton = document.createElement("button");
  decrementButton.className = "decrement-button";
  decrementButton.type = "button";
  decrementButton.tabIndex = -1;
  decrementButton.textContent = "▼";
  decrementButton.onmousedown = (e) => {
    e.preventDefault();
    startLongPress(-step);
  };
  decrementButton.onmouseup = stopLongPress;
  decrementButton.onmouseleave = stopLongPress;

  inputWrapper.appendChild(input);
  inputWrapper.appendChild(incrementButton);
  inputWrapper.appendChild(decrementButton);

  const handleOutsideClick = (event) => {
    if (!inputWrapper.contains(event.target)) {
      closeInput();
    }
  };

  const handleOutsideTouch = (event) => {
    if (!inputWrapper.contains(event.target)) {
      closeInput();
    }
  };

  const closeInput = () => {
    if (isClosed) return;
    isClosed = true;

    let finalValue = parseImperialFractionToDecimal(input.value);
    finalValue = Math.max(min, Math.min(finalValue, max));
    onChange(finalValue);

    onClose();
    inputWrapper.remove();
    EventManager.removeEvent(objectName);
    document.removeEventListener("mousedown", handleOutsideClick, true);
    document.removeEventListener("touchstart", handleOutsideTouch, true);
    input.removeEventListener("keydown", onKeyDownCapture, true);
    input.removeEventListener("keypress", stopPropagationCapture, true);
    input.removeEventListener("keyup", stopPropagationCapture, true);
    window.__suspendShortcuts = false;
  };

  const stopPropagationCapture = (e) => {
    e.stopPropagation();
  };

  const onKeyDownCapture = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      closeInput();
      return;
    }
    e.stopPropagation();
  };

  input.addEventListener("keydown", onKeyDownCapture, true);
  input.addEventListener("keypress", stopPropagationCapture, true);
  input.addEventListener("keyup", stopPropagationCapture, true);

  EventManager.addEvent(
    EventManager.events.MOUSE_DOWN,
    closeInput,
    objectName,
    EventManager.priorities.OVERALL
  );
  document.addEventListener("mousedown", handleOutsideClick, true);
  document.addEventListener("touchstart", handleOutsideTouch, true);

  input.addEventListener("focus", () => { window.__suspendShortcuts = true; });
  input.addEventListener("blur",  () => { window.__suspendShortcuts = false; });

  setTimeout(() => input.focus(), 0);

  return inputWrapper;
}
